import { useState, useEffect, useCallback, useRef } from 'react';

interface LoadingStates extends Record<string, boolean> {
  sensor: boolean;
  oil: boolean;
  coolant: boolean;
  componentChange: boolean;
  alarms: boolean;
  visualAI: boolean;
}

interface UseCombinedLoadingStateProps {
  onLoadingStateChange?: (isLoading: boolean) => void;
  selectedComponentId: string | null;
  selectedPart: string | null;
  alarmsLoading?: boolean;
  alarmsFetching?: boolean;
}

interface UseCombinedLoadingStateReturn {
  combinedLoadingState: boolean;
  handleSensorLoadingStateChange: (isLoading: boolean) => void;
  handleOilLoadingStateChange: (isLoading: boolean) => void;
  handleCoolantLoadingStateChange: (isLoading: boolean) => void;
  handleVisualAILoadingStateChange: (isLoading: boolean) => void;
  loadingStates: LoadingStates;
}

/**
 * Custom hook to manage combined loading states for sensor fusion components
 */
export function useCombinedLoadingState({
  onLoadingStateChange,
  selectedComponentId,
  selectedPart,
  alarmsLoading = false,
  alarmsFetching = false,
}: UseCombinedLoadingStateProps): UseCombinedLoadingStateReturn {
  // Individual loading states
  const [sensorLoadingState, setSensorLoadingState] = useState(false);
  const [oilLoadingState, setOilLoadingState] = useState(false);
  const [coolantLoadingState, setCoolantLoadingState] = useState(false);
  const [componentChangeLoading, setComponentChangeLoading] = useState(false);
  const [visualAILoadingState, setVisualAILoadingState] = useState(false);

  // Track previous component/part values
  const prevComponentRef = useRef<string | null>(null);
  const prevSubcomponentRef = useRef<string | null>(null);

  // Detect component/part changes and set loading state
  useEffect(() => {
    const hasComponentChanged = prevComponentRef.current !== null && prevComponentRef.current !== selectedComponentId;

    const hasPartChanged = prevSubcomponentRef.current !== null && prevSubcomponentRef.current !== selectedPart;

    if (hasComponentChanged || hasPartChanged) {
      // Component/Part changed - trigger loading state
      setComponentChangeLoading(true);

      // Clear the component change loading after a brief moment
      const timer = setTimeout(() => {
        setComponentChangeLoading(false);
      }, 500);

      return () => clearTimeout(timer);
    }

    prevComponentRef.current = selectedComponentId;
    prevSubcomponentRef.current = selectedPart;
  }, [selectedComponentId, selectedPart]);

  // Combine all loading states
  const combinedLoadingState =
    sensorLoadingState ||
    oilLoadingState ||
    coolantLoadingState ||
    componentChangeLoading ||
    alarmsLoading ||
    alarmsFetching ||
    visualAILoadingState;

  // Notify parent of loading state changes
  useEffect(() => {
    onLoadingStateChange?.(combinedLoadingState);
  }, [combinedLoadingState, onLoadingStateChange]);

  // Loading state handlers
  const handleSensorLoadingStateChange = useCallback((isLoading: boolean) => {
    setSensorLoadingState(isLoading);
  }, []);

  const handleOilLoadingStateChange = useCallback((isLoading: boolean) => {
    setOilLoadingState(isLoading);
  }, []);

  const handleCoolantLoadingStateChange = useCallback((isLoading: boolean) => {
    setCoolantLoadingState(isLoading);
  }, []);

  const handleVisualAILoadingStateChange = useCallback((isLoading: boolean) => {
    setVisualAILoadingState(isLoading);
  }, []);

  return {
    combinedLoadingState,
    handleSensorLoadingStateChange,
    handleOilLoadingStateChange,
    handleCoolantLoadingStateChange,
    handleVisualAILoadingStateChange,
    loadingStates: {
      sensor: sensorLoadingState,
      oil: oilLoadingState,
      coolant: coolantLoadingState,
      componentChange: componentChangeLoading,
      alarms: alarmsLoading || alarmsFetching,
      visualAI: visualAILoadingState,
    },
  };
}
